import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Users,
  MapPin,
  Phone,
  ChevronRight,
  Clock,
  UserPlus,
} from "lucide-react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import {
  useCustomerSearch,
  useCustomersByIds,
  type CustomerWithLocations,
} from "./api";
import { useRecentCustomers } from "./useRecentCustomers";
import { CustomerAvatar } from "./CustomerAvatar";
import { AddCustomerModal, type CustomerSeed } from "./AddCustomerModal";
import { PageHeader } from "@/components/ui/PageHeader";
import { FromCallStrip } from "@/features/calls/AttachFromCallCard";
import { displayCustomerName } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Customer search view - dispatcher's home base.
 *
 * Search query is persisted in the URL (?q=...) so back-button preserves
 * state. Empty state shows recently-viewed customers for quick re-access.
 *
 * Result cards are designed for fast visual triage:
 *   - avatar circle with initials (visual identifier)
 *   - large customer name as the lead
 *   - address + phone as supporting info
 *   - badges for inactive / multi-location / etc
 *   - small custno tag for ESC cross-reference
 *   - chevron animates on hover to suggest forward motion
 */
export function CustomerSearchView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";

  const [query, setQuery] = useState(initialQ);
  const [addOpen, setAddOpen] = useState(false);
  const [seed, setSeed] = useState<CustomerSeed | null>(null); // CALLS-AI: from-the-call prefill
  const [debouncedQuery, setDebouncedQuery] = useState(initialQ);

  // CALLS-CREATE-01: ?new=1 (the live call dock's "Start a new customer" on
  // an unmatched caller) opens Add Customer immediately, with ?q= carried in
  // as the prefill (a phone-shaped q lands in the phone field). One-shot —
  // the flag is stripped from the URL so back/refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setAddOpen(true);
      const p = new URLSearchParams(searchParams);
      p.delete("new");
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 250ms debounce for the actual query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Mirror the debounced query into the URL so back button preserves state
  useEffect(() => {
    if (debouncedQuery.trim() === "") {
      if (searchParams.get("q")) setSearchParams({}, { replace: true });
    } else if (searchParams.get("q") !== debouncedQuery) {
      setSearchParams({ q: debouncedQuery }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // INACTIVE-OVERLAY (Seth 2026-08-13): inactive accounts (ESC flag or the
  // Pegasus mark) are HIDDEN from search by default — the whole point is a
  // dispatcher can't book onto a dead duplicate. This checkbox is the way in.
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, isFetching } = useCustomerSearch(debouncedQuery, { includeInactive });

  return (
    <div className="mx-auto max-w-5xl p-8">
      <PageHeader
        icon={Users}
        title="Customers"
        description="Find anyone by name, address, phone, or email — ESC and Pegasus together."
        actions={
        <span className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800"
        >
          <UserPlus className="h-4 w-4" /> Add Customer
        </button>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500/40"
          />
          Include inactive accounts
        </label>
        </span>
      }
      />

      {/* CALLS-AI-01: a recent unmatched call with AI notes becomes a
          create-ready strip — every field the caller stated, one tap. */}
      <FromCallStrip onStart={(s) => { setSeed(s); setAddOpen(true); }} />

      <div className="relative mb-8">
        <Search className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, phone, email, address, zip, or customer #..."
          aria-label="Search customers"
          className="h-14 pl-12 pr-12 text-base"
        />
        {isFetching && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
          </div>
        )}
      </div>

      {query.trim().length === 0 ? (
        <EmptyState
          onPick={(custno) =>
            navigate(`/customers/${custno}?from=recents`)
          }
        />
      ) : isLoading ? (
        <LoadingSkeleton />
      ) : !data || data.length === 0 ? (
        <NoResults query={query} onAdd={() => setAddOpen(true)} />
      ) : (
        <Results
          rows={data}
          onPick={(custno) => {
            // Carry from=search + the active query so prev/next on the
            // detail view can reconstruct this result list and cycle.
            const params = new URLSearchParams({
              from: "search",
              q: debouncedQuery,
            });
            navigate(`/customers/${custno}?${params.toString()}`);
          }}
        />
      )}
      {addOpen && <AddCustomerModal initialQuery={query} seed={seed ?? undefined} onClose={() => { setAddOpen(false); setSeed(null); }} />}
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (custno: string) => void }) {
  const { recents } = useRecentCustomers();
  const { data: recentCustomers, isLoading } = useCustomersByIds(recents);

  return (
    <div className="space-y-6">
      <Card className="p-8">
        <Users className="mx-auto h-10 w-10 text-slate-300" />
        <h3 className="mt-3 text-center text-sm font-medium text-slate-900">
          Start typing to find a customer
        </h3>
        {/* DESIGN-CALM-01: one line — what you can type, nothing more. */}
        <p className="mx-auto mt-2 max-w-md text-center text-xs text-slate-500">
          Name, phone, email, address, zip or customer number.
        </p>
      </Card>

      {recents.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            Recently viewed
          </div>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="h-20 animate-pulse bg-slate-100" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {(recentCustomers ?? []).map((c, i) => (
                <ResultCard
                  key={c.custno}
                  row={c}
                  index={i}
                  onPick={onPick}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function NoResults({ query, onAdd }: { query: string; onAdd: () => void }) {
  return (
    <Card className="p-10 text-center">
      <h3 className="text-sm font-medium text-slate-900">
        No customers match &ldquo;{query}&rdquo;
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        Try a different spelling, partial name, or different field.
      </p>
      {/* NEWCUST-UI: an empty search is often a NEW caller — one click turns
          what they typed into a customer record (prefilled). */}
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800"
      >
        <UserPlus className="h-4 w-4" /> Add &ldquo;{query}&rdquo; as a New Customer
      </button>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="h-20 animate-pulse bg-slate-100" />
      ))}
    </div>
  );
}

function Results({
  rows,
  onPick,
}: {
  rows: CustomerWithLocations[];
  onPick: (custno: string) => void;
}) {
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <ResultCard key={r.custno} row={r} index={i} onPick={onPick} />
      ))}
      <p className="px-1 pt-3 text-xs text-slate-500">
        Showing up to 50 results &middot; refine the search to narrow.
      </p>
    </div>
  );
}

function ResultCard({
  row,
  index,
  onPick,
}: {
  row: CustomerWithLocations;
  index: number;
  onPick: (custno: string) => void;
}) {
  const { push } = useRecentCustomers();

  const primaryLoc =
    row.locations?.find((l) => !l.location_inactive) ?? row.locations?.[0];
  const displayName = displayCustomerName(null, row.fullname);
  const address = primaryLoc?.add1
    ? `${primaryLoc.add1}${primaryLoc.city ? `, ${primaryLoc.city}` : ""}${primaryLoc.state ? `, ${primaryLoc.state}` : ""}`
    : null;
  const phone = primaryLoc?.phone1 ?? row.phone1 ?? null;
  const multiLoc = (row.locations?.length ?? 0) > 1;

  function handleClick() {
    push(row.custno);
    onPick(row.custno);
  }

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15, delay: Math.min(index * 0.02, 0.2) }}
      onClick={handleClick}
      className={cn(
        "group flex w-full items-center gap-4 rounded-xl border border-hairline bg-white p-5 text-left shadow-e1",
        "transition-all duration-200 ease-out-quart hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-e2 motion-reduce:hover:translate-y-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
      )}
    >
      <CustomerAvatar
        custno={row.custno}
        name={displayName}
        address={primaryLoc ?? null}
        size="md"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <h3 className="truncate text-lg font-semibold text-slate-900">
            {displayName}
          </h3>
          {row.customerinactive && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Inactive
            </span>
          )}
          {multiLoc && (
            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700">
              {row.locations.length} locations
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm text-slate-600">
          {address && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-slate-500" />
              {address}
            </span>
          )}
          {phone && (
            <span className="inline-flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-slate-500" />
              {phone}
            </span>
          )}
        </div>
        {/* Reserved space for rich badges (agreement, last visit, etc) -
            see Task #56. The empty div keeps cards consistent height. */}
      </div>

      <div className="flex flex-shrink-0 items-center gap-3">
        <span className="font-mono text-xs text-slate-500">{row.custno}</span>
        <ChevronRight className="h-4 w-4 text-slate-300 transition-all duration-150 ease-out-quart group-hover:translate-x-1 group-hover:text-brand-500" />
      </div>
    </motion.button>
  );
}
